import '.a{display:grid;}.ssr-plugin'
import '.hf{grid-template-columns:repeat(2, 1fr);}.ssr-plugin'
import '.g{gap:1rem;}.ssr-plugin'
import Column from '@app/components/layout/Column'
import { ContentSection } from '@app/components/blocks/contentSection/ContentSection'
import useMediaQuery from '@app/util/useMediaQuery'
import { ConceptCard } from '@app/components/blocks/card/ConceptCard'
import { InitiativeCard } from '@app/components/blocks/card/InitiativeCard'
import { PageMediaGallery } from '@app/components/blocks/mediaGallery/PageMediaGallery'
import { useTranslations } from '@i18n/useLanguage'
import { styled } from 'inlines'
import { SectionHeader } from '@app/components/blocks/sectionHeader/SectionHeader'
import { ServiceProviderViewModel } from 'functions/app/service-provider/view'
import { labelAddressBuilder } from '@app/util/helpers'
import { useQuery } from '@based/sdk/react'
import { pages } from '@app/util/links'

type Concept = ServiceProviderViewModel['serviceProvidersConcepts'][number]

const ConceptCardItem = ({
  concept,
  serviceProvider,
}: {
  concept: Concept
  serviceProvider: ServiceProviderViewModel
}) => {
  const followerCountQuery = useQuery('count-followers', {
    entityType: 'serviceProviderConcept',
    entityIds: [concept.id],
  })

  return (
    <ConceptCard
      image={concept.banner?.src}
      title={serviceProvider.name}
      description={serviceProvider.description}
      followers={concept.followers ?? []}
      followerCount={followerCountQuery.data?.[0] ?? 0}
      slug={concept.slug}
    />
  )
}

export const Overview = ({
  serviceProvider,
}: {
  serviceProvider: ServiceProviderViewModel
}) => {
  const isMobile = useMediaQuery()
  const largerGap = isMobile ? '2rem' : '3.5rem'
  const smallerGap = isMobile ? '1.5rem' : '2rem'
  const translations = useTranslations().app

  const blocks = serviceProvider.contentBlocks ?? []

  return (
    <Column gap={largerGap}>
      <Column gap={largerGap}>
        {blocks.map((block, index) => {
          const section = {
            type: 'text' as const,
            title: block.title,
            body: block.body,
            truncationThreshold: block.truncationThreshold || 0,
          }
          return <ContentSection key={block.id ?? index} section={section} />
        })}
      </Column>

      <Column gap={smallerGap} alignItems="stretch">
        <PageMediaGallery
          title={serviceProvider.name}
          images={serviceProvider.media}
          style={{ width: '100%' }}
        />
        {/*
          To uncomment: add `const locale = useLocale()` and `const [language] = useLanguage()`
          to this component, and import both from '@i18n/useLanguage'.
        */}
        {/*
        {serviceProvider.events?.length > 0 && (
          <div>
            <SectionHeader
              title={translations.serviceProviderPage.events}
              seeMore={translations.common.seeMore}
              toRoute={'#'}
            />
            <styled.div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: '2rem',
              }}
            >
              {serviceProvider.events &&
                serviceProvider.events.length > 0 &&
                serviceProvider.events.map((event) => (
                  <Event
                    key={event.uuid}
                    date={new Date(event.start).getDate()}
                    month={new Date(event.start).toLocaleDateString(locale, {
                      month: 'long',
                    })}
                    category={
                      typeof event.category === 'string'
                        ? event.category
                        : event.category[language]
                    }
                    title={
                      typeof event.title === 'string'
                        ? event.title
                        : event.title[language]
                    }
                    intro={
                      typeof event.intro === 'string'
                        ? event.intro
                        : event.intro[language]
                    }
                    time={`${new Date(event.start).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })} - ${new Date(event.end).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`}
                    location={event.location.address}
                    onClick={() => console.warn('Event clicked!')}
                  />
                ))}
            </styled.div>
          </div>
        )} */}

        {/* Our Initiatives */}
        {serviceProvider.initiatives?.length > 0 && (
          <div>
            <SectionHeader
              title={translations.serviceProviderPage.ourInitiatives}
              seeMore={translations.common.seeMore}
              toRoute={'#'}
            />
            <div className="a hf g"
            >
              {serviceProvider.initiatives.slice(0, 2)?.map((initiative) => (
                <InitiativeCard
                  key={initiative.uuid}
                  tagText={
                    initiative.searchingForMembers
                      ? translations.common.initiativeStatus.searchingForMembers
                      : translations.common.initiativeStatus
                          .notSearchingForMembers
                  }
                  image={initiative.images?.[0]?.src}
                  location={labelAddressBuilder(
                    initiative.location.address,
                    translations.common.notSpecified,
                  )}
                  phase={initiative.phase?.name}
                  title={initiative.title}
                  description={initiative.description}
                  variant="default"
                  to={pages.initiative(initiative.slug)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Our Concepts */}
        {serviceProvider.serviceProvidersConcepts?.length > 0 && (
          <div>
            <SectionHeader
              title={translations.serviceProviderPage.ourConcepts}
              seeMore={translations.common.seeMore}
              toRoute={'#'}
            />
            <Column alignItems="stretch">
              {serviceProvider.serviceProvidersConcepts
                .slice(0, 2)
                ?.map((concept) => (
                  <ConceptCardItem
                    key={concept.uuid}
                    concept={concept}
                    serviceProvider={serviceProvider}
                  />
                ))}
            </Column>
          </div>
        )}
      </Column>
    </Column>
  )
}
